import { toast } from '@apps/web/lib/app_notifications';

export type AdminAgentPayloadFile = {
    readonly id: string;
    readonly title: string;
    readonly filename: string;
    readonly value: unknown;
    readonly sourceLabel?: string;
};

export function formatPayloadBytes(bytes: number): string {
    if (bytes < 1_024) {
        return `${bytes} B`;
    }
    if (bytes < 1_024 * 1_024) {
        return `${(bytes / 1_024).toFixed(1)} KB`;
    }
    return `${(bytes / (1_024 * 1_024)).toFixed(1)} MB`;
}

export function getUtf8ByteSize(value: string): number {
    if (typeof TextEncoder !== 'undefined') {
        return new TextEncoder().encode(value).length;
    }
    return value.length;
}

export function stringifyPayload(value: unknown): {
    readonly text: string;
    readonly format: 'json' | 'text';
} {
    if (typeof value === 'string') {
        const trimmed = value.trim();
        if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
            try {
                return {
                    text: JSON.stringify(JSON.parse(value), null, 2),
                    format: 'json',
                };
            } catch {
                return { text: value, format: 'text' };
            }
        }
        return { text: value, format: 'text' };
    }

    try {
        const text = JSON.stringify(value, null, 2);
        return {
            text: text === undefined ? String(value) : text,
            format: 'json',
        };
    } catch {
        return { text: String(value), format: 'text' };
    }
}

export function copyToClipboard(label: string, value: string): void {
    if (typeof navigator === 'undefined' || !navigator.clipboard?.writeText) {
        toast.error('Clipboard unavailable', { persistNotification: false });
        return;
    }
    void navigator.clipboard
        .writeText(value)
        .then(() =>
            toast.success(`${label} copied`, { persistNotification: false }),
        )
        .catch(() =>
            toast.error(`Could not copy ${label.toLowerCase()}`, {
                persistNotification: false,
            }),
        );
}

export function sanitizeFilenamePart(value: string): string {
    return value.replace(/[^a-zA-Z0-9._-]+/g, '-').slice(0, 80);
}
